"use client";

/**
 * OptionField + OptionCardGroup — the configurable-field building blocks for the
 * experience config / create surfaces (the chassis-first core of the OptionField
 * family).
 *
 * - `OptionField` wraps any control in a labelled row: label (+ required mark),
 *   the control, and a helper line that flips to an error message when `error`
 *   is set.
 * - `OptionCardGroup` is the selectable option-card / radio-chip pattern, built
 *   on the Radix RadioGroup primitive so single-select semantics, roving focus,
 *   and the `radiogroup`/`radio` ARIA roles come for free and stay correct. The
 *   whole card is the click target; the checked card reads in `primary`.
 *
 * Brand tokens only; the checked highlight + hover are `transition-colors` so
 * they are safe under `prefers-reduced-motion`.
 *
 * The rest of the family (SwatchGroup / SegmentedOptions / QuantityStepper) and
 * the ConfigPanel shell are separate follow-ons.
 */
import * as React from "react";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import { Check } from "lucide-react";
import { cn } from "../lib/cn";
import { Label } from "../primitives/Label";

/* -------------------------------------------------------------------------- */
/* OptionField                                                                */
/* -------------------------------------------------------------------------- */

export interface OptionFieldProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Field label. */
  label?: React.ReactNode;
  /** `htmlFor` for the label, when the control has an id. */
  htmlFor?: string;
  /** Helper text under the control (hidden when `error` is set). */
  helper?: React.ReactNode;
  /** Error message; when set it replaces the helper and reads in `destructive`. */
  error?: React.ReactNode;
  /** Show a required mark next to the label. */
  required?: boolean;
}

export const OptionField = React.forwardRef<HTMLDivElement, OptionFieldProps>(
  ({ label, htmlFor, helper, error, required, children, className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col gap-1.5", className)} {...props}>
      {label != null ? (
        <Label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
          {label}
          {required ? <span className="text-destructive"> *</span> : null}
        </Label>
      ) : null}
      {children}
      {error != null ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : helper != null ? (
        <p className="text-xs text-muted-foreground">{helper}</p>
      ) : null}
    </div>
  ),
);
OptionField.displayName = "OptionField";

/* -------------------------------------------------------------------------- */
/* OptionCardGroup                                                            */
/* -------------------------------------------------------------------------- */

export interface OptionCardOption {
  value: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  /** Leading icon, tinted to `primary` when the card is selected. */
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface OptionCardGroupProps {
  options: OptionCardOption[];
  /** Controlled selected value. */
  value?: string;
  /** Uncontrolled initial value. */
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  name?: string;
  disabled?: boolean;
  /** Responsive columns for the card grid. */
  columns?: 1 | 2 | 3;
  className?: string;
}

const COLS = { 1: "", 2: "sm:grid-cols-2", 3: "sm:grid-cols-3" } as const;

export const OptionCardGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  OptionCardGroupProps
>(function OptionCardGroup(
  { options, value, defaultValue, onValueChange, name, disabled, columns = 1, className },
  ref,
) {
  return (
    <RadioGroupPrimitive.Root
      ref={ref}
      data-slot="option-card-group"
      className={cn("grid gap-2", COLS[columns], className)}
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      name={name}
      disabled={disabled}
    >
      {options.map(option => (
        <RadioGroupPrimitive.Item
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          className={cn(
            "group flex items-start gap-3 rounded-lg border border-border bg-card p-3 text-left",
            "transition-colors hover:border-foreground/30 motion-reduce:transition-none",
            "data-[state=checked]:border-primary data-[state=checked]:bg-primary/5",
            "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:pointer-events-none disabled:opacity-50",
          )}
        >
          {option.icon != null ? (
            <span className="mt-0.5 shrink-0 text-muted-foreground group-data-[state=checked]:text-primary [&_svg]:size-5">
              {option.icon}
            </span>
          ) : null}
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-sm font-medium text-foreground">{option.label}</span>
            {option.description != null ? (
              <span className="text-xs text-muted-foreground">{option.description}</span>
            ) : null}
          </span>
          <RadioGroupPrimitive.Indicator className="ml-auto flex shrink-0 items-center text-primary">
            <Check className="size-4" />
          </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
});
